import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { Button, Checkbox } from '../../../components'
import { recordUnloadWindow } from '../../../services'
import { nowForDatetimeInput, wallClockToIso, isoToWallClock } from '../../../utils/warehouseDates'
import { translateAppointmentError } from '../../../utils/inboundAppointments'

const T = 'replenishments.inbound_appointments.unload'

// Records the real unload window for a booked/checked_in appointment. The API
// echoes unload_started_at/unload_ended_at to ops viewers (D10.1) on both the
// PUT response and the nested appointment the section fetches — the SERVER is
// the record, and the prefills come from the echoed fields so the recorded
// start is authoritative across devices. "Now" is only the fallback when
// nothing is recorded yet.
// Merge semantics: unload_started_at is always sent; unload_ended_at only
// when the operator opts in — omitting it preserves any stored value, so
// "start now, finish later" is two saves.
export const UnloadWindowPanel = ({ appointmentId, timeZone, unloadStartedAt = null, unloadEndedAt = null, initialNow }) => {
  const { i18n } = useTranslation()
  const [savedWindow, setSavedWindow] = useState(() =>
    unloadStartedAt ? { startedAt: isoToWallClock(unloadStartedAt, timeZone), endedAt: isoToWallClock(unloadEndedAt, timeZone) } : null,
  )
  const [startedAt, setStartedAt] = useState(() => savedWindow?.startedAt || nowForDatetimeInput(timeZone, initialNow))
  const [endedAt, setEndedAt] = useState(() => savedWindow?.endedAt || nowForDatetimeInput(timeZone, initialNow))
  const [includeEnd, setIncludeEnd] = useState(() => !!savedWindow?.endedAt)
  const [errorMsg, setErrorMsg] = useState(null)

  // The PUT echoes the recorded window (D10.1) — the saved marker mirrors what
  // the SERVER stored; the submitted values are only a fallback for a drifted
  // echo (fields absent from the response).
  const mutation = useMutation(({ payload }) => recordUnloadWindow(appointmentId, payload), {
    onSuccess: (data, { saved }) => {
      setSavedWindow({
        startedAt: data?.unload_started_at ? isoToWallClock(data.unload_started_at, timeZone) : saved.startedAt,
        endedAt: data?.unload_ended_at ? isoToWallClock(data.unload_ended_at, timeZone) : saved.endedAt,
      })
      setErrorMsg(null)
    },
    onError: error => setErrorMsg(translateAppointmentError(i18n, error)),
  })

  const handleSave = () => {
    setErrorMsg(null)
    const payload = { unload_started_at: wallClockToIso(startedAt, timeZone) }
    if (includeEnd) payload.unload_ended_at = wallClockToIso(endedAt, timeZone)
    mutation.mutate({ payload, saved: { startedAt, endedAt: includeEnd ? endedAt : savedWindow?.endedAt || null } })
  }

  // Opting in hours after mount must not silently submit the stale
  // mount-time prefill (final-review D9) — re-stamp with the REAL now, never
  // the initialNow used only to make mount-time prefills testable. A
  // server-recorded end is kept as-is (it IS the record).
  const handleIncludeEndChange = isChecked => {
    setIncludeEnd(isChecked)
    if (isChecked && !savedWindow?.endedAt) setEndedAt(nowForDatetimeInput(timeZone))
  }

  // Contract: end >= start (a sub-minute unload records as end == start).
  // Accidental zero-length submissions are guarded by the D9 restamp above.
  const endBeforeStart = includeEnd && endedAt < startedAt

  const inputClasses = 'shadow-sm block sm:text-sm rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50'

  return (
    <section className="flex-1 bg-white p-5" data-testid="unload-window-panel">
      <h1 className="text-lg mb-3 leading-6 font-medium text-gray-900">{i18n.t(`${T}.title`)}</h1>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="unload-started-input">
            {i18n.t(`${T}.started_label`)}
          </label>
          <div className="flex gap-2">
            <input
              id="unload-started-input"
              data-testid="unload-started-input"
              type="datetime-local"
              className={inputClasses}
              value={startedAt}
              onChange={e => setStartedAt(e.target.value)}
            />
            <Button type="secondary" dense onClick={() => setStartedAt(nowForDatetimeInput(timeZone))} className="px-3">
              {i18n.t(`${T}.now`)}
            </Button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="unload-ended-input">
            {i18n.t(`${T}.ended_label`)}
          </label>
          <div className="flex gap-2">
            <input
              id="unload-ended-input"
              data-testid="unload-ended-input"
              type="datetime-local"
              className={inputClasses}
              value={endedAt}
              onChange={e => setEndedAt(e.target.value)}
              disabled={!includeEnd}
            />
            <Button type="secondary" dense onClick={() => setEndedAt(nowForDatetimeInput(timeZone))} disabled={!includeEnd} className="px-3">
              {i18n.t(`${T}.now`)}
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-2">
        <Checkbox label={i18n.t(`${T}.include_end`)} checked={includeEnd} onChange={handleIncludeEndChange} />
      </div>

      <div className="mt-3 flex items-center gap-4">
        <Button loading={mutation.isLoading} disabled={mutation.isLoading || !startedAt || (includeEnd && !endedAt) || endBeforeStart} onClick={handleSave}>
          {i18n.t(`${T}.save`)}
        </Button>
        {savedWindow && (
          <span data-testid="unload-saved-badge" className="text-green-600 font-medium">
            {i18n.t(`${T}.saved`)}
          </span>
        )}
      </div>

      {errorMsg && (
        <div data-testid="unload-error" className="font-bold text-red-500 text-sm mt-2">
          {errorMsg}
        </div>
      )}
    </section>
  )
}
